import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./authentication.provider";
import { axiosInstance } from "../api/axiosInstance";
import { useApi } from "../hooks/api.hook";
import { isAxiosError } from "axios";
import { SignInToCheckoutComponent } from "../components/signInToCheckout";
import { CartStorageUtil, supportsHalfSlot } from "../utils/cart-util";
import { useLocation, useNavigate } from "react-router";
import { PageLoader } from "../components/loaders";
import { Mixpanel } from "../utils/mixpanel";
import {
  AddressManagementModalComponent,
  useAddress,
} from "../components/addressManagement/addressManagement";
import { activateNotification } from "../utils/notifications-utils";

export const CartContext = createContext({});

export const CartProvider = ({ children }) => {
  const location = useLocation();
  const [loadingCart, setLoadingCart] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const { isAuthenticated, isGuestUser, user } = useAuth();
  const { isAddressModalOpen, setIsAddressModalOpen, toggleAddressModal } =
    useAddress();
  const api = useApi();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState(() =>
    CartStorageUtil.getGuestCart()
  );

  useEffect(() => {
    if (!location.pathname.startsWith("/cart/checkout")) {
      setShowLoginModal(false);
    }
  }, [location.pathname]);


  useEffect(() => {
    if (!isAuthenticated && isGuestUser) {
      CartStorageUtil.setGuestCart(cartItems);
    }
  }, [cartItems, isAuthenticated, isGuestUser]);

  useEffect(() => {
    const fetchUserCart = async () => {
      setLoadingCart(true);
      try {
        if (isAuthenticated) {
          const { data } = await api.get("/cart");
          setCartItems(CartStorageUtil.normalizeCartResponse(data.items || []));
        } else {
          const guestCart = CartStorageUtil.getGuestCart();
          setCartItems(guestCart);

          if (guestCart.length > 0) {
            await validateGuestCartItems();
          }
        }
      } catch {
      } finally {
        setLoadingCart(false);
      }
    };
    fetchUserCart();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) syncLocalCartToServer();
  }, [isAuthenticated]);

  const makeApiRequest = async (apiCall, runOnClose) => {
    try {
      const result = await apiCall();
      const data = result.data;
      return {
        success: data.status || false,
        data,
        message:
          typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      };
    } finally {
      runOnClose?.();
    }
  };
  const trackCartEvent = (action, item, page) => {
    if (page === "CartDetailScreen") {
      const props = {
        id: item.split_buy_id,
        name: item?.title ?? item.split_buy_slug,
        status: item?.status,
        joined_status: item?.joined_status,
        reserved_slot: item?.reserved_slot,
        slots_left: item?.fractional_slots_left,
        split_buy_location: item?.split_buy_location,
        date_remaining: item?.join_period_end_date,
        cost_per_split: item?.price,
      };

      action === "add"
        ? Mixpanel.track_split_buy_add_to_cart(props)
        : Mixpanel.track_split_buy_remove_from_cart(props);
    } else {
      const props = {
        split_buy_id: item.split_buy_id,
        split_buy_name: item?.title ?? item?.split_buy_slog,
        screen: page,
      };

      action === "add"
        ? Mixpanel.track_home_add_to_cart(props)
        : Mixpanel.track_home_remove_from_cart(props);
    }
  };

  const addToCart = async (item, runOnClose, page) => {
    if (actionLoading) return;
    setActionLoading(true);

    try {
      const id = item.split_buy_id;

      if (isAuthenticated) {
        const cartItemId = findCartItemIdBySplitBuyId(id);

        if (cartItemId) {
          await removeFromCart(cartItemId);
          trackCartEvent("remove", item, page);
          return { success: true, message: "Item removed from cart" };
        }

        const response = await makeApiRequest(
          () =>
            api.post("/cart", {
              splitBuy: id,
              cartType: "split_buy",
            }),
          runOnClose
        );

        if (response.success) {
          CartStorageUtil.setCartQuantity(id, item.slot ?? item.quantity ?? 1);
          const { data } = await api.get("/cart");
          setCartItems(CartStorageUtil.normalizeCartResponse(data.items || []));
          trackCartEvent("add", item, page);
          return response;
        }
        return response;
      } else {
        const existsIndex = cartItems.findIndex((i) => i.split_buy_id === id);
        if (existsIndex >= 0) {
          const updated = cartItems.filter((_, index) => index !== existsIndex);
          CartStorageUtil.removeCartQuantity(id);
          setCartItems(updated);
          CartStorageUtil.setGuestCart(updated);
          trackCartEvent("remove", item, page);
          return { success: true, message: "Item removed from cart" };
        } else {
          const updated = [...cartItems, item];
          CartStorageUtil.setCartQuantity(id, item.slot ?? item.quantity ?? 1);
          setCartItems(updated);
          CartStorageUtil.setGuestCart(updated);
          trackCartEvent("add", item, page);
          return { success: true, message: "Added to cart" };
        }
      }
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };

  const leaveSplitGroup = async (splitBuyId, runOnClose) => {
    if (actionLoading && !isAuthenticated) return;
    setActionLoading(true);

    try {
      if (isAuthenticated) {
        return await makeApiRequest(() =>
          api.patch(`/split-buy/leave-split-buy/${splitBuyId}`)
        );
      }
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };

  const findCartItemIdBySplitBuyId = (splitBuyId) => {
    const item = cartItems.find((i) => i.split_buy_id === splitBuyId);
    return item?._id;
  };

  const removeFromCart = async (itemId) => {
    setActionLoading(true);
    try {
      const removedItem = cartItems.find((item) => item._id === itemId);
      const updated = cartItems.filter((i) => i._id !== itemId);
      if (isAuthenticated) {
        await api.delete(`/cart/${itemId}`);
      } else {
        CartStorageUtil.setGuestCart(updated);
      }
      CartStorageUtil.removeCartQuantity(removedItem?.split_buy_id);
      setCartItems(updated);
    } finally {
      setActionLoading(false);
    }
  };

  const clearCart = () => {
    setCartItems([]);
    CartStorageUtil.clearGuestCart();
    CartStorageUtil.clearCartQuantities();
  };

  const syncLocalCartToServer = async () => {
    const localCart = CartStorageUtil.getGuestCart();
    if (!localCart.length) return;

    localCart.forEach((item) => {
      CartStorageUtil.setCartQuantity(
        item.split_buy_id,
        item.slot ?? item.quantity ?? 1
      );
    });

    try {
      const results = await Promise.all(
        localCart.map((item) =>
          makeApiRequest(() =>
            api.post("/cart", {
              splitBuy: item.split_buy_id,
              cartType: "split_buy",
            })
          )
        )
      );

      const allSuccessful = results.every(result => result.success);

      if (allSuccessful) {
        const { data } = await api.get("/cart");
        const updatedCart = CartStorageUtil.normalizeCartResponse(data.items || []);

        setCartItems(updatedCart);

        CartStorageUtil.clearGuestCart();
        CartStorageUtil.clearGuestId();

      } else {
      }
    } catch (error) {
      console.error("Failed to sync cart:", error);
    }
  };

  const handleCheckout = async ({
    split_buy,
    addressIdOverride = null,
    pickUpAddressId = null,
    discount_code = undefined,
    order_notes = "",
    staff_email = "",
    delivery_state,
    skipAuthGuard = false,
    runOnClose,
  }) => {
    const isActuallyAuthenticated = isAuthenticated;
    const orderNotesPayload = order_notes?.trim()
      ? { order_notes: order_notes.trim() }
      : {};
    const staffEmailPayload = staff_email?.trim()
      ? { staff_email: staff_email.trim() }
      : {};
    sessionStorage.setItem(
      "pending_checkout",
      JSON.stringify({
        split_buy,
        addressIdOverride,
        pickUpAddressId,
        discount_code,
        ...orderNotesPayload,
        ...staffEmailPayload,
        delivery_state,
        timestamp: Date.now(),
        redirectTo: "/cart/checkout",
      })
    );

    if (!skipAuthGuard && !isActuallyAuthenticated && !isGuestUser) {
      setShowLoginModal(true);
      return {
        success: false,
        message: "Authentication required",
        requiresAuth: true,
      };
    }

    try {
      setActionLoading(true);

      const filterIncompatibleItems = (splitBuys, userState) => {
        const stateAliases = {
          Abuja: "Abuja",
          FCT: "Abuja",
          "Federal Capital Territory": "Abuja",
          "Federal Capital Territory (FCT)": "Abuja",
        };
        const normalizeState = stateAliases[userState] ?? userState;

        const incompatibleItems = [];
        const compatibleSplitBuys = [];
        let itemNames = "";
        let requiredStates = "";

        for (const sb of splitBuys) {
          const cartItem = cartItems.find(
            (item) => item.split_buy_id === sb.id
          );

          if (cartItem) {
            const cartLocation = cartItem.location;

            if (
              cartLocation &&
              normalizeState &&
              !cartLocation.includes(normalizeState)
            ) {
              incompatibleItems.push({
                id: cartItem._id,
                name: cartItem.title || "Unknown item",
                cartLocation: cartLocation,
                userState: normalizeState,
              });
            } else {
              compatibleSplitBuys.push(sb);
            }
          } else {
            compatibleSplitBuys.push(sb);
          }
        }

        if (incompatibleItems.length > 0) {
          itemNames = incompatibleItems.map((item) => item.name).join(", ");
          requiredStates = [
            ...new Set(
              incompatibleItems.map((item) => {
                const locationParts = item.cartLocation.split(", ");
                return locationParts[locationParts.length - 1];
              })
            ),
          ].join(", ");
        }

        return {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates,
        };
      };

      const buildCheckoutResult = (checkoutData, message) => {
        const checkout = checkoutData?.data || checkoutData;
        const discountApplied = !discount_code || Number(checkout?.discount_applied || 0) > 0;

        if (!discountApplied) {
          activateNotification({
            title: "Discount not applied",
            body: "This discount code was not applied to your order.",
          });
        }

        return {
          success: checkoutData.status || false,
          data: checkoutData,
          discountApplied,
          message:
            message ||
            (typeof checkoutData.message === "object"
              ? checkoutData.message[0]
              : checkoutData.message),
        };
      };

      if (isActuallyAuthenticated) {
        const getItemStates = (item) => {
          if (!item) return [];
          const choices = item?.choice_of_delivery_locations;
          if (Array.isArray(choices) && choices.length) {
            return choices.map((s) => String(s).trim()).filter(Boolean);
          }
          if (item?.location) {
            return String(item.location)
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean);
          }
          return [];
        };

        const normalize = (value) => String(value).trim().toLowerCase();
        const getAllowedStatesIntersection = (splitBuys) => {
          if (!splitBuys?.length) return [];
          const firstItem = cartItems.find(
            (item) => item.split_buy_id === splitBuys[0]?.id
          );
          const firstStates = getItemStates(firstItem);
          if (!firstStates.length) return [];
          let intersection = firstStates.map(normalize);
          for (let i = 1; i < splitBuys.length; i += 1) {
            const cartItem = cartItems.find(
              (item) => item.split_buy_id === splitBuys[i]?.id
            );
            const states = getItemStates(cartItem);
            if (!states.length) return [];
            const normalized = states.map(normalize);
            intersection = intersection.filter((state) =>
              normalized.includes(state)
            );
            if (!intersection.length) return [];
          }
          const originalMap = new Map(
            firstStates.map((state) => [normalize(state), state])
          );
          return Array.from(new Set(intersection))
            .map((state) => originalMap.get(state) || state)
            .filter(Boolean);
        };

        const allowedStates = getAllowedStatesIntersection(split_buy);
        if (!allowedStates.length) {
          activateNotification({
            title: "Checkout Issue",
            body:
              "Your cart items have different delivery states. Please check out items with the same state one at a time.",
          });
          setActionLoading(false);
          return {
            success: false,
            message:
              "Your cart items have different delivery states. Please check out items with the same state one at a time.",
          };
        }
        if (pickUpAddressId) {
          const { data: checkoutData } = await api.post("/cart/combine", {
            split_buy,
            pick_up_address_id: pickUpAddressId,
            discount_code,
            ...orderNotesPayload,
            ...staffEmailPayload,
          });

          return buildCheckoutResult(checkoutData);
        }

        let selectedAddress = null;

        if (addressIdOverride) {
          const { data: addressData } = await api.get(
            `/address/${addressIdOverride}`
          );
          selectedAddress = addressData?.address || addressData || null;
          if (
            selectedAddress?.state &&
            !allowedStates
              .map((s) => normalize(s))
              .includes(normalize(selectedAddress.state))
          ) {
            activateNotification({
              title: "Checkout Issue",
              body:
                "Selected address state does not match the delivery states for your cart items. Please choose another address or check out items separately.",
            });
            setActionLoading(false);
            return {
              success: false,
              message:
                "Selected address state does not match cart delivery states.",
            };
          }
        } else {
          const { data: addressResponse } = await api.get("/address");
          const addresses = addressResponse?.addresses || [];

          if (addresses.length === 0) {
            setActionLoading(false);
            setShowAddressModal(true);
            return {
              success: false,
              message: "Please add a delivery address before checkout.",
            };
          }

          const normalizedAllowed = allowedStates.map((s) => normalize(s));
          selectedAddress =
            addresses
              .slice()
              .reverse()
              .find((addr) =>
                normalizedAllowed.includes(normalize(addr?.state || ""))
              ) || null;
        }
        if (!selectedAddress) {
          activateNotification({
            title: "Checkout Issue",
            body:
              "No saved address matches the delivery states for your cart items. Please add or select a matching address.",
          });
          setActionLoading(false);
          setShowAddressModal(true);

          return {
            success: false,
            message: "Please add a delivery address before checkout.",
          };
        }

        const {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates,
        } = filterIncompatibleItems(split_buy, selectedAddress.state);

        if (incompatibleItems.length > 0) {
          split_buy = compatibleSplitBuys;

          if (split_buy.length === 0) {
            return {
              success: false,
              message:
                "No items available for delivery to your selected address.",
            };
          }
        }

        const addressId = selectedAddress._id;
        const { data: checkoutData } = await api.post("/cart/combine", {
          split_buy,
          address_id: addressId,
          discount_code,
          ...orderNotesPayload,
          ...staffEmailPayload,
        });

        return buildCheckoutResult(
          checkoutData,
          incompatibleItems.length > 0
            ? `Buyer's delivery location state (${selectedAddress.state}) is not in the allowed delivery locations (${requiredStates}) for ${itemNames}. The incompatible items have been removed from your cart.`
            : undefined
        );
      }
      else {
        const guestPickUpAddressId =
          pickUpAddressId ||
          user?.pick_up_location_id ||
          user?.pick_up_address_id ||
          null;
        const {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates,
        } = filterIncompatibleItems(split_buy, user?.state);

        if (incompatibleItems.length > 0) {
          split_buy = compatibleSplitBuys;

          if (split_buy.length === 0) {
            return {
              success: false,
              message:
                "No items available for delivery to your selected address.",
            };
          }
        }

        const { data: checkoutData } = await axiosInstance.post(
          "/cart/combine",
          {
            split_buy,
            guest_id: user?._id,
            discount_code,
            ...orderNotesPayload,
            ...staffEmailPayload,
            ...(guestPickUpAddressId
              ? { pick_up_address_id: guestPickUpAddressId }
              : {}),
          },
          {
            headers: {
              "guest-user-key": process.env.REACT_APP_API_GUEST_USER_KEY,
            },
          }
        );

        return buildCheckoutResult(
          checkoutData,
          incompatibleItems.length > 0
            ? `Buyer's delivery location state (${user?.state}) is not in the allowed delivery locations (${requiredStates}) for ${itemNames}. The incompatible items have been removed from your cart.`
            : undefined
        );
      }
    } catch (err) {
      setActionLoading(false);
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
          err.response?.data?.message ||
          err.message ||
          "Checkout failed. Try again.",
      };
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };


  const handlePendingCheckout = async ({
    skipAuthGuard = false,
    addressIdOverride,
  } = {}) => {
    const pendingCheckout = sessionStorage.getItem("pending_checkout");

    if (!pendingCheckout) return null;


    const payload = JSON.parse(pendingCheckout);

    return await handleCheckout({
      ...payload,
      addressIdOverride: addressIdOverride ?? payload.addressIdOverride,
      pickUpAddressId:
        payload.pickUpAddressId ||
        user?.pick_up_location_id ||
        user?.pick_up_address_id ||
        null,
      skipAuthGuard,
    });
  };
  const removeGuestCartItems = (itemIds) => {
    cartItems
      .filter((item) => itemIds.includes(item._id))
      .forEach((item) => CartStorageUtil.removeCartQuantity(item.split_buy_id));
    const updated = cartItems.filter((i) => !itemIds.includes(i._id));
    setCartItems(updated);
    CartStorageUtil.setGuestCart(updated);
  };

  const toggleCheckout = () => setShowLoginModal((prev) => !prev);

  const updateCartItemQuantity = async (cartItemId, newQuantity) => {
    setActionLoading(true);
    try {
      if (isAuthenticated) {
        const cartItem = cartItems.find((item) => item._id === cartItemId);
        CartStorageUtil.setCartQuantity(cartItem?.split_buy_id, newQuantity);
        setCartItems((currentItems) => currentItems.map((item) =>
          item._id === cartItemId
            ? { ...item, slot: newQuantity, quantity: newQuantity }
            : item
        ));
        return { success: true, message: "Quantity updated" };
      }

      const guestCart = CartStorageUtil.getGuestCart();
      const updatedCart = guestCart.map((item) =>
        item._id === cartItemId
          ? {
            ...item,
            slot: newQuantity,
            quantity: newQuantity,
          }
          : item
      );

      setCartItems(updatedCart);
      CartStorageUtil.setGuestCart(updatedCart);
      const updatedItem = updatedCart.find((item) => item._id === cartItemId);
      CartStorageUtil.setCartQuantity(updatedItem?.split_buy_id, newQuantity);
      return { success: true, message: "Quantity updated" };
    } finally {
      setActionLoading(false);
    }
  };

  const fetchCartItemDetails = async (id, runOnClose) => {
    try {
      const { data } = await api.get(`/split-buy/details?slug=${id}`);
      return {
        success: data.status || false,
        data: data,
        message:
          typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
          err.response?.data?.message ||
          err.message ||
          "Checkout failed. Try again.",
      };
    } finally {
      runOnClose?.();
    }
  };

  const validateGuestCartItems = async () => {
    if (isAuthenticated || !cartItems.length) return;

    try {
      const validationPromises = cartItems.map(async (item) => {
        try {
          const { data } = await axiosInstance.get(
            `/split-buy/details?slug=${item.split_buy_slug}`
          );

          if (data.status && data.data) {
            const splitBuy = data.data;
            const availableSlots = splitBuy.fractional_slots_left;
            const buyInAmount = splitBuy.split_cost_per_slot;
            const halfSlotSupported = supportsHalfSlot(splitBuy.allow_half_slot, splitBuy.half_slot_supported);
            const minimumQuantity = halfSlotSupported ? 0.5 : 1;
            const isSplitMember = splitBuy.is_split_member;
            const isNotExpired =
              new Date(splitBuy?.delivery_date).getTime() > Date.now();

            const isAvailable =
              splitBuy.split_buy_status !== "completed" &&
              availableSlots >= minimumQuantity &&
              !isSplitMember
            //&& isNotExpired;

            if (isAvailable) {
              const requestedQuantity = Number(item.quantity) || 1;
              const validRequestedQuantity = !halfSlotSupported && requestedQuantity < 1
                ? 1
                : requestedQuantity;
              const maxPurchasableQuantity = halfSlotSupported
                ? Math.floor(availableSlots * 2) / 2
                : Math.floor(availableSlots);
              const maxAllowedQuantity = Math.min(
                validRequestedQuantity,
                maxPurchasableQuantity
              );

              const updatedItem = {
                ...item,
                available_slots: availableSlots,
                allow_half_slot: halfSlotSupported,
                price: buyInAmount,
                title: splitBuy.product?.title || item.title,
                location:
                  splitBuy.split_buy_product_location?.string ||
                  splitBuy.choice_of_delivery_locations?.[0] ||
                  item.location,
                delivery_date: splitBuy.delivery_date,
                join_period_end_date: splitBuy.join_period_end_date,
                fractional_number_of_buy_ins_attained: splitBuy.fractional_number_of_buy_ins_attained,
                quantity: maxAllowedQuantity,
                slot: maxAllowedQuantity,
                split_buy_status: splitBuy.split_buy_status,
                total_slots: splitBuy.number_of_buy_ins_required,
                last_validated: new Date().toISOString(),
              };

              return {
                type: "validated",
                item: updatedItem,
                wasAdjusted: maxAllowedQuantity !== requestedQuantity,
                adjustment:
                  maxAllowedQuantity !== requestedQuantity
                    ? {
                      name: splitBuy.product?.title || item.title,
                      original: requestedQuantity,
                      new: maxAllowedQuantity,
                    }
                    : null,
              };
            } else {
              return {
                type: "removed",
                splitBuyId: item.split_buy_id,
                name:
                  splitBuy.product?.title || item.title || item.split_buy_slug,
              };
            }
          } else {
            return {
              type: "removed",
              splitBuyId: item.split_buy_id,
              name: item.title || item.split_buy_slug,
            };
          }
        } catch (error) {
          return {
            type: "error",
            item: {
              ...item,
              last_validated: new Date().toISOString(),
              validation_error: true,
            },
          };
        }
      });

      const results = await Promise.all(validationPromises);
      // console.log(results, 'Validation Results...')
      const validatedItems = [];
      const removedItems = [];
      const updatedItems = [];

      results.forEach((result) => {
        switch (result.type) {
          case "validated":
            validatedItems.push(result.item);
            CartStorageUtil.setCartQuantity(
              result.item.split_buy_id,
              result.item.quantity ?? result.item.slot ?? 1
            );
            if (result.wasAdjusted && result.adjustment) {
              updatedItems.push(result.adjustment);
            }
            break;
          case "removed":
            CartStorageUtil.removeCartQuantity(result.splitBuyId);
            removedItems.push(result.name);
            break;
          case "error":
            validatedItems.push(result.item);
            break;
        }
      });

      if (
        validatedItems.length !== cartItems.length ||
        updatedItems.length > 0
      ) {
        setCartItems(validatedItems);
        CartStorageUtil.setGuestCart(validatedItems);
      }

      return validatedItems;
    } catch (error) {
      return cartItems;
    }
  };
  const handleAddressAdded = async () => {
    const pendingResult = await handlePendingCheckout({ skipAuthGuard: false });
    if (pendingResult?.success) {
      navigate("/cart/checkout", { state: pendingResult.data?.data });
    } else {
      activateNotification({
        title: "Checkout Issue",
        body: pendingResult?.message || "Unable to process your checkout.",
      });
      navigate("/cart");
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        syncLocalCartToServer,
        handleCheckout,
        showLoginModal,
        loadingCart,
        actionLoading,
        setShowLoginModal,
        removeGuestCartItems,
        updateCartItemQuantity,
        fetchCartItemDetails,
        validateGuestCartItems,
        handlePendingCheckout,
        leaveSplitGroup,
      }}
    >
      <SignInToCheckoutComponent
        toggle={toggleCheckout}
        isOpen={showLoginModal}
      />
      {children}

      <AddressManagementModalComponent
        onAddressAdded={handleAddressAdded}
        view={"add"}
        isOpen={showAddressModal}
        toggle={() => setShowAddressModal(false)}
      />

      {actionLoading && (
        <div
          role="alert"
          aria-busy="true"
          className="!tw-z-[100000] position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <PageLoader />
        </div>
      )}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
